/**
 * Projektbilder für den Design-Editor (IndexedDB + transiente Uploads + Server-Liste).
 *
 * Modulweiter Zustand, weil das Bilder-Panel per v-if aus- und eingehängt wird, Drops auf die
 * Bühne aber auch bei geschlossenem Panel fertig werden müssen. Der Upload-Runner meldet direkt
 * nach complete-batch oft nur „processing“ (Server erzeugt Vorschauen noch) und lässt den
 * IndexedDB-Datensatz dann auf „uploading“ stehen – der normale Editor pollt das in
 * image-tool.vue nach, hier übernimmt das `reload()` über /upload/list.
 */
import { computed, ref } from 'vue'
import type { UploadRef } from '@pfe/design-renderer'
import { useImageDB, type ImageRecord } from '../utils/imageDb'
import { importImageToDb } from '../services/imageImportService'
import { getUploadsRunner } from '../runtime/uploads-runner.client'
import { onUploadEvent } from '../utils/uploadBus'
import { useTransientUploadStore } from '../store/useTransientUploadStore'
import { useDesignStore } from '../store/useDesignStore'
import { getWidgetConfig } from '../runtime/widgetConfig'
import { markRemoteDeleted } from '../services/uploadService'
import {
  fetchProjectUploads,
  findServerDeletedRecords,
  invalidateProjectUploadsCache,
  mapRemoteUploadToRecordMeta,
  mergeServerAndLocalImageRecord,
} from '../services/projectUploadBridge'

export type DesignUploadContext = { projectCode: string; productId: number; categoryId: number; product: any | null }

const POLL_MS = 2000
const ACCEPTED_FILE = /^image\/|\.(heic|heif|tiff?)$/i

const records = ref<ImageRecord[]>([])
const busy = ref(false)
let ctx: DesignUploadContext | null = null
let users = 0
let offBus: (() => void) | null = null
let busTimer: ReturnType<typeof setTimeout> | null = null
let pollTimer: ReturnType<typeof setTimeout> | null = null
let reloadSeq = 0

export function isUploadReady(rec: ImageRecord): boolean {
  return rec.upload?.state === 'done' && !!(rec.upload?.fullscreenPreviewUrl || rec.upload?.remoteUrl)
}

export function uploadRefOf(rec: ImageRecord): UploadRef | null {
  const url = rec.upload?.fullscreenPreviewUrl || rec.upload?.remoteUrl
  if (!url) return null
  return { uploadId: rec.id, url, width: rec.width || rec.editorWidth, height: rec.height || rec.editorHeight }
}

export function isAcceptedImageFile(file: File): boolean {
  return ACCEPTED_FILE.test(file.type || '') || ACCEPTED_FILE.test(file.name || '')
}

function isPending(rec: ImageRecord): boolean {
  const state = String(rec.upload?.state || '')
  return !isUploadReady(rec) && state !== 'failed'
}

async function reload(): Promise<void> {
  if (!ctx) return
  const seq = ++reloadSeq
  const { projectCode } = ctx
  const db = useImageDB('imageDB')
  const transient = useTransientUploadStore()
  const cfg = getWidgetConfig() as any
  const editorBaseUrl = String(cfg?.editorBaseUrl || '').trim()

  const remoteLimit = 200
  const [fromDb, remoteOrNull] = await Promise.all([
    db.getByProjectCode(projectCode).catch(() => []) as Promise<ImageRecord[]>,
    editorBaseUrl && projectCode
      ? fetchProjectUploads({ editorBaseUrl, projectCode, limit: remoteLimit }).catch(() => null)
      : Promise.resolve(null),
  ])
  if (seq !== reloadSeq || !ctx || ctx.projectCode !== projectCode) return
  const remote = remoteOrNull || []

  const fromTransient = transient.all.filter((r: any) => !r.projectCode || r.projectCode === projectCode) as ImageRecord[]
  const map = new Map<string, ImageRecord>()
  for (const r of [...fromDb, ...fromTransient]) map.set(r.id, r)

  // Auf dem Server gelöschte Bilder (z. B. im Warenkorb der Website entfernt) lokal aufräumen.
  if (remoteOrNull && remoteOrNull.length < remoteLimit) {
    const serverIds = new Set(remoteOrNull.map((item) => String(item?.id || '')))
    const goneIds = findServerDeletedRecords([...map.values()], serverIds, projectCode).map((r) => r.id)
    if (goneIds.length) {
      markRemoteDeleted(goneIds)
      transient.removeMany(goneIds)
      for (const id of goneIds) {
        map.delete(id)
        void db.delete(id).catch(() => {})
      }
    }
  }

  for (const item of remote) {
    const id = String(item?.id || '')
    if (!id) continue
    const server = mapRemoteUploadToRecordMeta(item, { projectCode })
    const local = map.get(id) ?? null
    if (!local) {
      // z. B. per Handy-Upload ins Projekt gekommen
      if (isUploadReady(server)) map.set(id, server)
      continue
    }
    if (isUploadReady(local) || !isUploadReady(server)) continue
    const merged = mergeServerAndLocalImageRecord(server, local)
    map.set(id, merged)
    // Fertigen Zustand persistieren, damit der Datensatz nicht dauerhaft auf „uploading“ steht.
    void db.update(id, { upload: merged.upload, width: merged.width ?? local.width, height: merged.height ?? local.height } as any).catch(() => {})
  }

  records.value = [...map.values()].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))

  const info: Record<string, { widthPx: number; heightPx: number; status?: string }> = {}
  for (const r of records.value) info[r.id] = { widthPx: r.width || 0, heightPx: r.height || 0, status: r.upload?.state === 'done' ? 'done' : (r.upload?.state || 'queued') }
  useDesignStore().setUploadInfo(info)

  schedulePoll()
}

function schedulePoll() {
  if (pollTimer) clearTimeout(pollTimer)
  pollTimer = null
  if (!ctx || !records.value.some(isPending)) return
  pollTimer = setTimeout(() => {
    pollTimer = null
    const cfg = getWidgetConfig() as any
    if (ctx) invalidateProjectUploadsCache({ editorBaseUrl: String(cfg?.editorBaseUrl || ''), projectCode: ctx.projectCode })
    void reload()
  }, POLL_MS)
}

/** Importiert Dateien in die Upload-Pipeline; liefert die neuen Datensatz-IDs. */
async function importFiles(files: File[] | FileList | null | undefined): Promise<string[]> {
  const list = Array.from(files ?? []).filter(isAcceptedImageFile)
  if (!list.length || !ctx) return []
  const { product, productId, categoryId, projectCode } = ctx
  busy.value = true
  const ids: string[] = []
  try {
    for (const file of list) {
      const rec = await importImageToDb(file, { product, productId, categoryId, projectCode }).catch(() => null)
      if (rec) ids.push(rec.id)
    }
    getUploadsRunner()?.startAuto?.(600)
    await reload()
  } finally {
    busy.value = false
  }
  return ids
}

/** Vom Design-Editor (Seite) aufgerufen; hält Polling und Bus-Abo am Leben. */
function start(next: DesignUploadContext) {
  const changed = !ctx || ctx.projectCode !== next.projectCode
  ctx = { ...next }
  users += 1
  if (!offBus) {
    offBus = onUploadEvent(() => {
      if (busTimer) clearTimeout(busTimer)
      busTimer = setTimeout(() => { void reload() }, 400)
    })
  }
  if (changed) records.value = []
  void reload()
}

function updateContext(patch: Partial<DesignUploadContext>) {
  if (!ctx) return
  const changed = patch.projectCode != null && patch.projectCode !== ctx.projectCode
  ctx = { ...ctx, ...patch }
  if (changed) { records.value = []; void reload() }
}

function stop() {
  users = Math.max(0, users - 1)
  if (users > 0) return
  offBus?.()
  offBus = null
  if (busTimer) clearTimeout(busTimer)
  if (pollTimer) clearTimeout(pollTimer)
  busTimer = pollTimer = null
  ctx = null
}

export function useDesignUploads() {
  return {
    records,
    busy,
    readyIds: computed(() => new Set(records.value.filter(isUploadReady).map((r) => r.id))),
    reload,
    importFiles,
    start,
    stop,
    updateContext,
  }
}
